'use client'

import type { Api } from '@cruz-com/lookup'
import _ from 'lodash'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2'
import ResponsivePagination from 'react-responsive-pagination'
import { abbNumber, encodeQuery } from '@/lib'

type Props = {
  meta: Api.Meta
  onPageNumberChanged?: (val: number) => void
} & React.ComponentPropsWithoutRef<'span'>

export default function Pagination({ meta, onPageNumberChanged }: Props) {
  const router = useRouter()
  const pathname = usePathname()
  const params = useSearchParams()
  const query = _.assign({}, _.pick(meta, 'page_number'), Object.fromEntries(params))
  const [currentPage, setCurrentPage] = useState(_.toNumber(query?.page_number) || 1)

  useEffect(() => {
    setCurrentPage(_.toNumber(query?.page_number) || 1)
  }, [query?.page_number])

  const totalPages = useMemo(() => {
    if (!meta?.total_pages) {
      return 0
    }
    const maxPages = Math.floor(10000 / (meta.page_size || 24))
    return Math.min(meta.total_pages, maxPages)
  }, [meta?.total_pages, meta?.page_size])

  if (!meta || _.isEmpty(meta)) {
    return null
  }

  const { total_pages, total_records_matched } = meta || {}

  const handlePageClick = (pageNumber: number) => {
    if (onPageNumberChanged) {
      onPageNumberChanged(pageNumber)
    }
    setCurrentPage(pageNumber)
    router.push(`${pathname}?${encodeQuery({ ...query, page_number: pageNumber })}`)
  }

  return (
    <nav aria-label='Pagination Navigation' className='mx-auto max-w-[600px] pt-10 text-center'>
      <ResponsivePagination
        total={totalPages}
        current={currentPage}
        onPageChange={handlePageClick}
        previousLabel={
          <div className='flex items-center gap-x-1 text-xs/6'>
            <HiChevronLeft className='size-5' aria-hidden='true' />
            <span>Previous</span>
          </div>
        }
        ariaPreviousLabel='Previous Page'
        nextLabel={
          <div className='flex items-center gap-x-1 text-xs/6'>
            <span>Next</span>
            <HiChevronRight className='size-5' aria-hidden='true' />
          </div>
        }
        ariaNextLabel='Next Page'
      />

      <div className='mt-6 text-center text-sm/6 font-light' aria-live='polite' aria-atomic='true'>
        Total <span className='font-medium'>{abbNumber(total_records_matched || 0, 2)}</span> results,{' '}
        <span className='font-medium'>{total_pages?.toLocaleString()}</span> page{(total_pages || 0) > 1 ? 's' : ''}.
      </div>
    </nav>
  )
}
